import React, { useState } from 'react';
import { Play, Clock, Users, Sparkles, Video } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useArenaRecordings, ArenaRecording } from '@/hooks/useArenaRecordings';
import { ArenaTypeBadge, ArenaType } from './ArenaTypeBadge';
import { formatDistanceToNow } from 'date-fns';

const formatDuration = (seconds: number | null): string => {
  if (!seconds) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  if (mins >= 60) {
    const hours = Math.floor(mins / 60);
    const remainingMins = mins % 60;
    return `${hours}h ${remainingMins}m`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

interface RecordingCardProps {
  recording: ArenaRecording;
  onPlay: (recording: ArenaRecording) => void;
}

const RecordingCard: React.FC<RecordingCardProps> = ({ recording, onPlay }) => {
  return (
    <div className="flex-shrink-0 w-64 bg-card rounded-xl border border-border overflow-hidden hover:border-primary/50 transition-colors group">
      {/* Thumbnail area */}
      <div className="relative h-36 bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center">
        <Video className="w-12 h-12 text-muted-foreground/50" />
        
        {/* Play overlay */}
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <Button
            size="icon"
            className="w-12 h-12 rounded-full bg-primary hover:bg-primary/90"
            onClick={() => onPlay(recording)}
          >
            <Play className="w-6 h-6 fill-primary-foreground" />
          </Button>
        </div>
        
        {/* Duration badge */}
        <div className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded flex items-center gap-1">
          <Clock className="w-3 h-3" />
          {formatDuration(recording.duration_seconds)}
        </div>
        
        {/* Arena type */}
        <div className="absolute top-2 left-2">
          <ArenaTypeBadge type={(recording.arena_type as ArenaType) || 'vibe_booth'} size="sm" />
        </div>
      </div>
      
      {/* Info */}
      <div className="p-3 space-y-2">
        <h4 className="font-semibold text-sm truncate">{recording.arena_name}</h4>
        
        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Users className="w-3 h-3" />
              {recording.participant_count}
            </span>
            <span className="flex items-center gap-1">
              <Sparkles className="w-3 h-3 text-pink-500" />
              {recording.pop_count}
            </span>
          </div>
          <span>
            {formatDistanceToNow(new Date(recording.recorded_at), { addSuffix: true })}
          </span>
        </div>
      </div>
    </div>
  );
};

interface PlaybackModalProps {
  recording: ArenaRecording | null;
  open: boolean;
  onClose: () => void;
}

const PlaybackModal: React.FC<PlaybackModalProps> = ({ recording, open, onClose }) => {
  if (!recording) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="w-5 h-5" />
            {recording.arena_name}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Video player area */}
          <div className="aspect-video bg-black rounded-lg flex items-center justify-center">
            {recording.video_url ? (
              <video
                src={recording.video_url}
                controls
                autoPlay
                className="w-full h-full rounded-lg"
              />
            ) : (
              <div className="text-center text-muted-foreground">
                <Video className="w-16 h-16 mx-auto mb-2 opacity-50" />
                <p>Recording preview not available</p>
              </div>
            )}
          </div>
          
          {/* Stats */}
          <div className="flex items-center justify-between bg-muted/50 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <ArenaTypeBadge type={(recording.arena_type as ArenaType) || 'vibe_booth'} />
            </div>
            
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <span>{formatDuration(recording.duration_seconds)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-muted-foreground" />
                <span>{recording.participant_count} participants</span>
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-pink-500" />
                <span>{recording.pop_count} pops</span>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ArenaRecordingsList: React.FC = () => {
  const { recordings, loading } = useArenaRecordings();
  const [selectedRecording, setSelectedRecording] = useState<ArenaRecording | null>(null);
  const [playbackOpen, setPlaybackOpen] = useState(false);

  const handlePlay = (recording: ArenaRecording) => {
    setSelectedRecording(recording);
    setPlaybackOpen(true);
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <h3 className="text-lg font-semibold flex items-center gap-2">
          <Video className="w-5 h-5 text-primary" />
          Past Recordings
        </h3>
        <div className="flex gap-4 overflow-hidden">
          {[1, 2, 3].map((i) => (
            <div key={i} className="w-64 h-48 bg-muted animate-pulse rounded-xl flex-shrink-0" />
          ))}
        </div>
      </div>
    );
  }

  if (recordings.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3">
      <h3 className="text-lg font-semibold flex items-center gap-2">
        <Video className="w-5 h-5 text-primary" />
        Past Recordings
        <span className="text-xs font-normal text-muted-foreground ml-2">
          Replay sessions you missed
        </span>
      </h3>
      
      <ScrollArea className="w-full">
        <div className="flex gap-4 pb-4">
          {recordings.map((recording) => (
            <RecordingCard
              key={recording.id}
              recording={recording}
              onPlay={handlePlay}
            />
          ))}
        </div>
      </ScrollArea>
      
      <PlaybackModal
        recording={selectedRecording}
        open={playbackOpen}
        onClose={() => setPlaybackOpen(false)}
      />
    </div>
  );
};
